import React, { useState, useMemo } from "react"
import { useJobs } from "../../../hooks/DataFetching/useJobs"
import { useCheckApplicationStatus } from "../../../hooks/DataFetching/useCheckApplicationStatus"
import { JobCard } from "./JobCard"
import { JobModal } from "./JobModal"
import { isJobAcceptingApplications } from "./jobDisplay"
import { Input } from "../../ui/input"
import { StatusMessage } from "../../ui/StatusMessage"
import { BriefcaseBusiness, Search } from "lucide-react"

export const JobsPage = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedJob, setSelectedJob] = useState(null)
  const { jobs, loading, error } = useJobs()
  const jobList = useMemo(() => (Array.isArray(jobs) ? jobs : []), [jobs])
  const eligibleJobs = useMemo(
    () => jobList.filter((job) => isJobAcceptingApplications(job)),
    [jobList],
  )

  // Memoize the jobIds
  const jobIds = useMemo(() => eligibleJobs.map((job) => job.id), [eligibleJobs])

  const { applicationStatus, loading: statusLoading, error: statusError } = useCheckApplicationStatus(jobIds)

  const filteredJobs = useMemo(
    () =>
      eligibleJobs.filter(
        (job) =>
          (job.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (job.groupName || "").toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [eligibleJobs, searchTerm],
  )

  if (loading || statusLoading) {
    return (
      <StatusMessage
        tone="loading"
        title="Loading jobs"
        message="Please wait while we fetch current job listings."
      />
    )
  }

  if (error || statusError) {
    return (
      <StatusMessage
        tone="error"
        title="Could not load jobs"
        message={error || statusError}
      />
    )
  }

  return (
    <main className="tyk-page">
      <div className="tyk-page-shell">
        <section className="tyk-hero-panel">
          <div>
            <span className="tyk-kicker">
              <BriefcaseBusiness size={16} aria-hidden="true" />
              Work marketplace
            </span>
            <h1 className="tyk-hero-title">Jobs</h1>
            <p className="tyk-hero-copy">
              Find paid roles across TYK communities, compare responsibilities, and apply with confidence.
            </p>
          </div>
          <div className="tyk-stat min-w-[160px]">
            <span>Open roles</span>
            <strong>{filteredJobs.length}</strong>
          </div>
        </section>

        <section className="tyk-panel">
          <div className="tyk-toolbar">
            <div>
              <span className="tyk-kicker">
                <Search size={15} aria-hidden="true" />
                Search
              </span>
              <p className="m-0 text-sm text-slate-400">
                Showing {filteredJobs.length} of {eligibleJobs.length} open role{eligibleJobs.length === 1 ? "" : "s"}.
              </p>
            </div>
            <div className="w-full max-w-md">
              <label className="sr-only" htmlFor="job-search">Search job listings</label>
              <Input
                id="job-search"
                type="text"
                placeholder="Search by title or group..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tyk-input"
              />
            </div>
          </div>

          {filteredJobs.length === 0 ? (
            <StatusMessage
              tone="empty"
              title="No jobs found"
              message="Try a different search term or check back later."
            />
          ) : (
            <div className="tyk-grid">
              {filteredJobs.map((job) => (
                <JobCard key={job.id} job={job} onClick={() => setSelectedJob(job)} hasApplied={applicationStatus[job.id]} />
              ))}
            </div>
          )}
        </section>
        {selectedJob && <JobModal job={selectedJob} isOpen={!!selectedJob} onClose={() => setSelectedJob(null)} />}
      </div>
    </main>
  )
}

